import FetchBase from '../Api/FetchBase'
import { observable, action, toJS, computed } from 'mobx'
import {
  API_ENDPOINTS,
  DEFAULT_PAGE_START,
  ERROR_CODE,
} from '../Utils/constants'
import { generateFormData } from '../Utils/helpers'

export default class TaskTemplateStore extends FetchBase {
  @observable taskTemplatFoldersSearchText = ''
  @observable taskFoldersLoader = false
  @observable taskTemplatFoldersCurrentPage = DEFAULT_PAGE_START
  @observable taskTemplatFoldersList = []
  @observable taskTemplatFoldersTotal = 0
  @observable taskTemplateFolderActionLoader = false
  @observable taskTemplatFoldersDropdownList = []
  @observable moveTaskTemplateByFIdLoader = false

  @observable taskTemplates = []
  @observable allTaskGroups = []
  @observable allTaskGroupsForDropdown = []
  @observable taskTypes = []
  @observable taskSubTypes = []

  @observable tasks = []
  @observable taskLoader = false

  @observable addTaskLoader = false
  @observable addTaskTemplateLoader = false

  @observable currentFolderTaskGroup = []
  @observable alreadyTakenTaskGroups = []
  @observable groupBasedTaskTemplates = []
  @observable groupBasedTaskTemplatesLoader = undefined
  @observable groupBasedCachedTaskTemplates = {}

  @observable apiTasksData = []
  @observable taskSearchText = ''

  @observable taskGoupForTemplateLoader = false

  @observable importTaskTemp = []
  @observable importTaskTempFailures = []
  @observable importTaskTempFromFileLoader = false

  @action.bound
  setTaskTemplateFoldersSearchText = (value = '') => {
    this.taskTemplatFoldersSearchText = value
  }

  @action.bound
  setTaskTemplateCurrentPage = (page = DEFAULT_PAGE_START) => {
    this.taskTemplatFoldersCurrentPage = page
  }

  @action.bound
  getTaskTemplateFolders = async (signal) => {
    try {
      this.taskFoldersLoader = true
      let options = {}
      this.taskTemplatFoldersSearchText &&
        (options['search'] = this.taskTemplatFoldersSearchText)
      let response = await this.get(
        API_ENDPOINTS.FOLDER_TASK_TEMPLATE,
        {
          pageNo: Math.max(this.taskTemplatFoldersCurrentPage - 1, 0),
          ...options,
        },
        signal
      )
      if (response.status) {
        this.taskTemplatFoldersList = response?.data?.taskTemplateFolders
        this.taskTemplatFoldersTotal = response?.data?.total || 0
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.taskFoldersLoader = false
    }
  }

  @action.bound
  addTaskTemplateFolder = async (values) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.FOLDER_ADD_TASK_TEMPLATE,
        values
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  addTaskTemplateFolderLocally = async (values) => {
    this.taskTemplatFoldersList = [values, ...this.taskTemplatFoldersList]
    if (this.taskTemplatFoldersList.length > 10) {
      this.taskTemplatFoldersList.pop()
    }
    this.taskTemplatFoldersTotal++
  }

  @action.bound
  updateTaskTemplateFolder = async (values) => {
    try {
      this.taskTemplateFolderActionLoader = true
      let response = await this.post(
        API_ENDPOINTS.FOLDER_UPDATE_TASK_TEMPLATE,
        values
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.taskTemplateFolderActionLoader = false
    }
  }

  @action.bound
  updateTaskTemplateFolderLocally = async (id, values) => {
    this.taskTemplatFoldersList = (this.taskTemplatFoldersList || []).map(
      (folder) => {
        if (folder.id === id) {
          return values
        } else {
          return folder
        }
      }
    )
  }

  @action.bound
  deleteTaskTemplateFolder = async (data = {}) => {
    try {
      this.taskTemplateFolderActionLoader = true
      let response = await this.post(
        API_ENDPOINTS.FOLDER_DELETE_TASK_TEMPLATE,
        data
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.taskTemplateFolderActionLoader = false
    }
  }

  @action.bound
  getTaskTemplateFoldersDropdown = async (signal) => {
    try {
      let response = await this.get(
        API_ENDPOINTS.GET_DROPDOWN_TASK_TEMP_FOLDER,
        {
          pageLimit: 0,
        },
        signal
      )
      if (response.status) {
        this.taskTemplatFoldersDropdownList =
          response?.data?.taskTemplateFolders
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  moveTaskTemplateByFolderId = async (folderId = undefined, data = {}) => {
    try {
      let options = data
      this.moveTaskTemplateByFIdLoader = true
      options['taskTemplateFolderId'] = folderId
      let response = await this.post(
        API_ENDPOINTS.UPDATE_TASK_TO_SPACE_TEMPLATE,
        options
      )
      if (response.status) {
        return [true, response.status]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.moveTaskTemplateByFIdLoader = false
    }
  }

  // End folder features

  @action.bound
  setTaskSearchText = (value = '') => {
    this.taskSearchText = value
  }

  @computed
  get alreadyTakenGroupsComputed() {
    let searchText = (this.taskSearchText || '').toLowerCase()
    let newTaskGroups = []
    newTaskGroups = (this.alreadyTakenTaskGroups || []).filter((taskGroup) => {
      if (!searchText) {
        return true
      }
      let result = false
      let tasksObject =
        this.groupBasedCachedTaskTemplates?.[taskGroup?.id]?.tasks || []

      for (let index = 0; index < tasksObject?.length || 0; index++) {
        let task = tasksObject[index]
        let name = (task?.name || '')?.toLowerCase()
        if (name.includes(searchText)) {
          result = true
          break
        }
      }
      return result
    })
    return newTaskGroups
  }

  @computed
  get totalTaskTemplates() {
    let searchText = (this.taskSearchText || '').toLowerCase()
    let newCount = 0
    ;(this.alreadyTakenTaskGroups || []).forEach((taskGroup) => {
      let tasksObject =
        this.groupBasedCachedTaskTemplates?.[taskGroup?.id]?.tasks || []
      for (let index = 0; index < tasksObject?.length || 0; index++) {
        let task = tasksObject[index]
        let name = (task?.name || '')?.toLowerCase()
        if (name?.includes(searchText)) {
          newCount += 1
        }
      }
    })
    return newCount
  }

  @action.bound
  getCountForTasksOfTGroup = (groupId) => {
    let searchText = (this.taskSearchText || '').toLowerCase()
    let filteredTask = (
      this.groupBasedCachedTaskTemplates[groupId]?.tasks || []
    )?.filter((taskTemplate) => {
      let name = (taskTemplate?.name || '')?.toLowerCase()
      return (name || '').includes(searchText)
    })
    return filteredTask?.length
  }

  @action.bound
  getGroupBasesTaskTemplates = async (groupId) => {
    try {
      this.groupBasedTaskTemplatesLoader = groupId
      let searchText = (this.taskSearchText || '').toLowerCase()
      /** Remove these lines when apis are supported and uncomment below ones */
      if (this.groupBasedCachedTaskTemplates[groupId]) {
        this.groupBasedTaskTemplates =
          this.groupBasedCachedTaskTemplates[groupId].tasks
        this.groupBasedTaskTemplates = (
          this.groupBasedTaskTemplates || []
        ).filter((taskTemplate) => {
          let name = (taskTemplate?.name || '').toLowerCase()
          return name.includes(searchText)
        })

        return [true, this.groupBasedTaskTemplates]
      } else {
        return [false, undefined]
      }
      /** Remove these lines when apis are supported and uncomment below ones */

      /*let response = await this.get(
        API_ENDPOINTS.GET_GROUP_BASE_TASK_TEMPLATES,
        {
          groupId,
        }
      )
      if (response.status) {
        this.groupBasedTaskTemplates = response.data
        return [true, response.data]
      }
      return [false, undefined]*/
    } catch (err) {
      return [false, undefined]
    } finally {
      this.groupBasedTaskTemplatesLoader = undefined
    }
  }

  @action.bound
  getTemplateDataById = (templateId) => {
    return (this.taskTemplates || []).find(
      (taskTemplate) => taskTemplate?.id === templateId
    )
  }

  @action.bound
  getAllTaskTemplates = async (networkOnly = true, fId) => {
    let temp = {}
    fId && (temp['taskTemplateFolderId'] = fId)
    if (!networkOnly) {
      if (this.taskTemplates.length) {
        return [true, this.taskTemplates]
      }
    }
    try {
      let response = await this.get(API_ENDPOINTS.GET_ALL_TASK_TEMPLATES, {
        ...temp,
        pageLimit: 0,
      })
      if (response.status) {
        this.taskTemplates = response.data.taskTemplates
        return [response.status, response.data.taskTemplates]
      }
      return [false, undefined]
    } catch (err) {
      this.taskTemplates = []
      return [false, undefined]
    } finally {
      // this.restructureForDropdown(this.taskTemplates)
    }
  }

  @action.bound
  getAllTaskGroups = async (
    networkOnly = true,
    taskGroupFolderId = undefined
  ) => {
    if (!networkOnly) {
      if (this.allTaskGroups.length) {
        return [true, this.allTaskGroups]
      }
    }

    try {
      this.taskGoupForTemplateLoader = true
      let response = await this.get(API_ENDPOINTS.GET_TASK_GROUPS_LIST, {
        taskGroupFolderId,
      })
      if (response.status) {
        this.allTaskGroups = response.data
        return [true, response.data]
      }
      this.allTaskGroups = []
      return [false, undefined]
    } catch (err) {
      this.allTaskGroups = []
      return [false, undefined]
    } finally {
      this.taskGoupForTemplateLoader = false
    }
  }

  @action.bound
  __unifiedTaskGroups = (taskTemplates = []) => {
    let groupsWithTasks = {}
    let groups = []
    ;(taskTemplates || []).forEach((taskTemplate) => {
      const { inTaskGroup = {} } = taskTemplate
      const taskGroupId = inTaskGroup?.id
      if (taskGroupId) {
        if (groupsWithTasks[taskGroupId]) {
          let _ = groupsWithTasks[taskGroupId].tasks?.push(taskTemplate)
        } else {
          groupsWithTasks[taskGroupId] = {
            ...inTaskGroup,
            tasks: [taskTemplate],
          }
          groups.push({
            ...inTaskGroup,
          })
        }
      }
    })
    return { groupsWithTasks, groups }
  }

  @action.bound
  passCurrentFolderTaskGroup = (taskGroup) => {
    this.currentFolderTaskGroup = taskGroup
  }

  resetTaskPrefilledFromTaskTemp = () => {
    this.currentFolderTaskGroup = []
    this.taskTemplates = []
  }

  @action.bound
  getFilterBasedData = () => {
    let { groupsWithTasks, groups } = this.__unifiedTaskGroups(
      this.apiTasksData
    )
    this.groupBasedCachedTaskTemplates = groupsWithTasks
    this.alreadyTakenTaskGroups = groups
  }

  @action.bound
  __getAllTasksSimulator = async (taskTemplateFolderId = undefined) => {
    let [response, responseData] = await this.__getAllTasks(
      taskTemplateFolderId
    )
    if (response) {
      this.apiTasksData = [...responseData]
      this.getFilterBasedData()
    }
  }

  @action.bound
  __getAllTasksSimulatorExport = async (values) => {
    try {
      let temp = { ...values }
      temp['search'] = (this.taskSearchText || '').toLowerCase()

      let response = await this.get(API_ENDPOINTS.__GET_ALL_TASKS_EXPORT, {
        ...temp,
        pageLimit: 0,
      })
      if (response.status) {
        return [true, response.data || []]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  __getAllTasks = async (taskTemplateFolderId = undefined) => {
    try {
      let response = await this.get(API_ENDPOINTS.__GET_ALL_TASKS, {
        taskTemplateFolderId,
        pageLimit: 0,
      })
      if (response.status) {
        return [true, response.data?.taskTemplates || []]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getTaskTypes = async (taskGroupId) => {
    try {
      let response = await this.get(API_ENDPOINTS.GET_TASK_TYPES, {
        taskGroupId,
      })
      if (response.status) {
        this.taskTypes = response.data
        return [response.status, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getTaskSubTypes = async (taskTypeId) => {
    try {
      let response = await this.get(API_ENDPOINTS.GET_TASK_SUB_TYPES, {
        taskTypeId,
      })
      if (response.status) {
        this.taskSubTypes = response.data
        return [response.status, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  getAllTaks = async (spaceId) => {
    try {
      this.taskLoader = true
      let response = await this.get(API_ENDPOINTS.SPACE_TEMPLATE_TASKS, {
        spaceId,
        pageLimit: 0,
      })
      if (response.status) {
        this.tasks = response.data.taskTemplates
        return [true, response.data.taskTemplates || []]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.taskLoader = false
    }
  }

  @action.bound
  addNewTaskLocally = (task = {}) => {
    if (task?.id) {
      this.tasks = [...this.tasks, task]
    }
  }

  @action.bound
  addNewTask = async (values = {}) => {
    try {
      this.addTaskLoader = true
      const {
        spaceId,
        group,
        name,
        hours,
        picture = false,
        type,
        subtype,
        description,
      } = values

      let response = await this.post(API_ENDPOINTS.ADD_TASK_TO_SPACE_TEMPLATE, {
        spaceId,
        taskGroupId: group,
        name: name,
        totalHours: hours,
        belongsToSpace: true,

        imageRequired: picture,
        taskTypeId: type,
        taskSubTypeId: subtype,
        taskDescription: description || undefined,
      })
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.addTaskLoader = false
    }
  }

  @action.bound
  updateTaskLocally = (task = {}) => {
    if (task?.id) {
      this.tasks = (this.tasks || []).map((iTask) => {
        if (iTask?.id === task?.id) {
          return task
        } else {
          return iTask
        }
      })
    }
  }

  @action.bound
  updateTask = async (id, values = {}) => {
    try {
      this.addTaskLoader = true
      const {
        name,
        hours,
        picture = false,
        type,
        subtype,
        description,
        taskTemplateFolderId,
        spaceId,
      } = values

      let response = await this.post(
        API_ENDPOINTS.UPDATE_TASK_TO_SPACE_TEMPLATE,
        {
          id,
          name: name,
          totalHours: hours,
          imageRequired: picture,
          taskTypeId: type,
          taskSubTypeId: subtype,
          taskDescription: description || '',
          taskTemplateFolderId: taskTemplateFolderId || undefined,
          spaceId: spaceId || undefined,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.addTaskLoader = false
    }
  }

  @action.bound
  deleteTaskLocally = (id) => {
    if (id) {
      this.tasks = (this.tasks || []).filter((task) => task?.id !== id)
    }
  }

  @action.bound
  deleteTask = async (id, values = {}) => {
    try {
      let response = await this.post(
        API_ENDPOINTS.DELETE_TASK_TO_SPACE_TEMPLATE,
        {
          id,
          ...values,
        }
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    }
  }

  @action.bound
  addTaskGroupLocally = (values = []) => {
    let temp = []
    ;(this.currentFolderTaskGroup || []).forEach((group) => {
      if (values?.groups.includes(group?.id)) {
        group['taskGroupFolderId'] = values?.taskGroupFolderId
        temp.push(group)
      }
    })
    this.alreadyTakenTaskGroups = [...this.alreadyTakenTaskGroups, ...temp]

    /** Remove this line when apis are supported */
    ;(temp || []).forEach((taskGroup) => {
      this.groupBasedCachedTaskTemplates[taskGroup.id] = {
        ...taskGroup,
        tasks: [],
      }
    })
    /** Remove this line when apis are supported */
  }

  @action.bound
  deleteTaskTemplateLocallyForGroup = (id) => {
    if (id) {
      let taskObject = {}
      this.groupBasedTaskTemplates = (
        this.groupBasedTaskTemplates || []
      ).filter((taskTemplate) => {
        if (taskTemplate?.id === id) {
          taskObject = taskTemplate
        }
        return id !== taskTemplate?.id
      })

      /** Remove these lines when apis are supported */
      let groupId = taskObject?.taskGroupId
      if (groupId) {
        let groupObject = this.groupBasedCachedTaskTemplates[groupId]
        let allTasksForThisGroup = (groupObject?.tasks || []).filter(
          (taskTemplate) => {
            return taskTemplate?.id !== id
          }
        )
        groupObject.tasks = allTasksForThisGroup
      }
      /** Remove these lines when apis are supported */
    }
  }

  @action.bound
  addLocallyTaskToGroupTemplate = (task, activeGroupId) => {
    let groupId = task?.taskGroupId

    if (groupId) {
      let alreadyCached = this.groupBasedCachedTaskTemplates[groupId]
      if (alreadyCached) {
        let _ = alreadyCached?.tasks?.push(task)
      } else {
        this.addTaskGroupLocally([groupId])
        let _ = this.groupBasedCachedTaskTemplates[groupId]?.tasks?.push(task)
      }
    }
    activeGroupId = Number(activeGroupId)
    /** Remove these lines when apis are supported */
    if (task?.id && groupId && activeGroupId && activeGroupId === groupId) {
      this.getGroupBasesTaskTemplates(groupId)
    }
    /** Remove these lines when apis are supported */
  }

  @action.bound
  updateTaskLocallyForTaskGroup = (task) => {
    if (task?.id) {
      /** Remove these lines when apis are supported */
      let groupId = task?.taskGroupId

      if (groupId) {
        let groupObject = this.groupBasedCachedTaskTemplates[groupId]
        let allTasksForThisGroup = (groupObject?.tasks || []).map(
          (taskTemplate) => {
            if (taskTemplate?.id === task?.id) {
              return task
            } else {
              return taskTemplate
            }
          }
        )
        groupObject.tasks = allTasksForThisGroup
      }

      /** Remove these lines when apis are supported */
      this.getGroupBasesTaskTemplates(task?.taskGroupId)
    }
  }

  @action.bound
  addNewTaskForGroup = async (values = {}) => {
    try {
      this.addTaskLoader = true
      this.addTaskTemplateLoader = true

      const {
        group,
        name,
        hours,
        picture = false,
        type,
        subtype,
        description,
        taskTemplateFolderId,
      } = values
      let temp = {
        taskGroupId: group,
        name: name,
        belongsToSpace: false,
        imageRequired: picture,
        taskTypeId: type,
        taskSubTypeId: subtype,
        taskDescription: description || undefined,
        taskTemplateFolderId: taskTemplateFolderId || undefined,
      }
      hours && (temp['totalHours'] = hours)

      let response = await this.post(
        API_ENDPOINTS.ADD_TASK_TO_SPACE_TEMPLATE,
        temp
      )
      if (response.status) {
        return [true, response.data]
      }
      return [false, undefined]
    } catch (err) {
      return [false, undefined]
    } finally {
      this.addTaskLoader = false
      this.addTaskTemplateLoader = false
    }
  }

  // Import task template
  @action.bound
  importTaskTempFromFile = async (values) => {
    try {
      this.importTaskTempFromFileLoader = true
      let formDataValues = generateFormData(values)
      let response = await this.postWithFormData(
        API_ENDPOINTS.IMPORT_TASKS_TEMP_FROM_FILE,
        formDataValues
      )
      if (response.status) {
        this.importTaskTemp = response.data || []
        this.importTaskTempFailures =
          {
            headerMessage: response?.message || '',
            failedErrors: response.data?.failures || [],
            fileUpload: response.status,
            isError: true,
          } || []
        return [true, response.data]
      } else {
        return [false, undefined]
      }
    } catch (err) {
      if (err?.error?.statusCode === ERROR_CODE?.MENUAL_CODE) {
        this.importTaskTempFailures =
          {
            headerMessage: err?.message,
            failedErrors: null,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      } else {
        this.importTaskTempFailures =
          {
            headerMessage: err?.message,
            failedErrors: err?.error,
            fileUpload: false,
            isError: true,
          } || []
        return [false, undefined]
      }
    } finally {
      this.importTaskTempFromFileLoader = false
    }
  }

  @action.bound
  importTaskTempFromFileReset = async () => {
    this.importTaskTemp = []
    this.importTaskTempFailures = []
  }

  @action.bound
  resetStore = () => {
    this.taskTemplatFoldersDropdownList = []
    this.moveTaskTemplateByFIdLoader = false
    this.taskTemplatFoldersSearchText = ''
    this.taskFoldersLoader = false
    this.taskTemplatFoldersCurrentPage = DEFAULT_PAGE_START
    this.taskTemplatFoldersList = []
    this.taskTemplatFoldersTotal = 0
    this.taskTemplateFolderActionLoader = false
    this.taskTemplates = []
    this.allTaskGroups = []
    this.allTaskGroupsForTemplateDropdown = []
    this.taskTypes = []
    this.taskSubTypes = []
    this.tasks = []
    this.taskLoader = false
    this.addTaskLoader = false
    this.addTaskTemplateLoader = false
    this.currentFolderTaskGroup = []
    this.alreadyTakenTaskGroups = []
    this.groupBasedTaskTemplates = []
    this.groupBasedTaskTemplatesLoader = undefined
    this.groupBasedCachedTaskTemplates = {}
    this.apiTasksData = []
    this.taskSearchText = ''
    this.taskGoupForTemplateLoader = false

    this.importTaskTemp = []
    this.importTaskTempFailures = []
    this.importTaskTempFromFileLoader = false
  }
}
